import { useEffect, useState } from "react";

/**
 * Subscribes to a CSS media query and returns whether it currently matches.
 * Safe for this Vite SPA (no SSR) — initial state reads `matchMedia` directly
 * so the first render already reflects the real viewport.
 *
 * A host without `matchMedia` (jsdom, an ancient WebView) reports "no match"
 * rather than throwing: a layout hook must never be the reason a page fails to
 * render, and "no match" is the narrow/default branch every caller can handle.
 */
export function useMediaQuery(query: string): boolean {
	const [matches, setMatches] = useState(
		() => window.matchMedia?.(query).matches ?? false,
	);

	useEffect(() => {
		const mql = window.matchMedia?.(query);
		if (!mql) return;
		const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
		setMatches(mql.matches);
		mql.addEventListener("change", onChange);
		return () => mql.removeEventListener("change", onChange);
	}, [query]);

	return matches;
}

/** Tailwind's `md` breakpoint is 768px; "mobile" is anything narrower. */
export function useIsMobile(): boolean {
	return !useMediaQuery("(min-width: 768px)");
}
